/* eslint-disable */

import React, { useMemo, useState } from "react";
import {
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Collapse,
  Divider,
  FormControl,
  FormControlLabel,
  FormLabel,
  Grid,
  Paper,
  Radio,
  RadioGroup,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import AccountBalanceOutlinedIcon from "@mui/icons-material/AccountBalanceOutlined";
import BadgeOutlinedIcon from "@mui/icons-material/BadgeOutlined";
import CalendarTodayOutlinedIcon from "@mui/icons-material/CalendarTodayOutlined";
import CheckCircleOutlinedIcon from "@mui/icons-material/CheckCircleOutlined";
import CreditScoreOutlinedIcon from "@mui/icons-material/CreditScoreOutlined";
import DownloadIcon from "@mui/icons-material/Download";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import HomeOutlinedIcon from "@mui/icons-material/HomeOutlined";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import PersonOutlineOutlinedIcon from "@mui/icons-material/PersonOutlineOutlined";
import PhoneIphoneOutlinedIcon from "@mui/icons-material/PhoneIphoneOutlined";
import ShieldOutlinedIcon from "@mui/icons-material/ShieldOutlined";
import WcOutlinedIcon from "@mui/icons-material/WcOutlined";
import moment from "moment";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import PageTitle from "examples/NewDesign/PageTitle";
import { PageTitles, Colors } from "utils/Constants";
import Sessions from "utils/Sessions";
import { fetchCibil } from "redux/Thunks/CibilThunk";
import { useDispatch } from "react-redux";

const formatDate = (raw) => {
  if (!raw) return "-";

  const cleaned = String(raw).replace(/\D/g, "");
  if (cleaned.length === 8) {
    if (cleaned.startsWith("19") || cleaned.startsWith("20")) {
      return moment(cleaned, "YYYYMMDD", true).isValid()
        ? moment(cleaned, "YYYYMMDD").format("DD/MM/YYYY")
        : "-";
    }

    return moment(cleaned, "DDMMYYYY", true).isValid()
      ? moment(cleaned, "DDMMYYYY").format("DD/MM/YYYY")
      : "-";
  }

  return moment(raw).isValid() ? moment(raw).format("DD/MM/YYYY") : String(raw);
};

const formatNumber = (value) => {
  if (value === 0 || value) {
    const num = Number(value);
    return Number.isNaN(num) ? value : num.toLocaleString("en-IN");
  }
  return "-";
};

const formatCurrency = (value) => {
  const formatted = formatNumber(value);
  return formatted === "-" ? "-" : `Rs. ${formatted}`;
};

const getCreditReport = (base) => {
  if (Array.isArray(base?.credit_report)) return base.credit_report[0] || {};
  return base?.credit_report || {};
};

const getScore = (base, creditReport) =>
  base?.credit_score ??
  creditReport?.SCORE?.FCIREXScore ??
  creditReport?.scores?.[0]?.score ??
  creditReport?.scores?.[0]?.value ??
  "-";

const getGender = (applicant, fallback) => {
  if (applicant?.Gender_Code === "1") return "Male";
  if (applicant?.Gender_Code === "2") return "Female";
  return applicant?.Gender || fallback || "-";
};

const getScoreLabel = (score) => {
  const numericScore = Number(score);
  if (Number.isNaN(numericScore)) return "-";
  if (numericScore >= 750) return "Excellent";
  if (numericScore >= 700) return "Good";
  if (numericScore >= 650) return "Fair";
  return "Needs Work";
};

export const normalizeReport = (rawReport, formData, gender, meta = {}) => {
  const base = rawReport || {};
  const creditReport = getCreditReport(base);
  const header = creditReport?.CreditProfileHeader || {};
  const currentApplication = creditReport?.Current_Application?.Current_Application_Details || {};
  const applicant = currentApplication?.Current_Applicant_Details || {};
  const address = currentApplication?.Current_Applicant_Address_Details || {};
  const caisAccount = creditReport?.CAIS_Account || {};
  const caisSummary = caisAccount?.CAIS_Summary || {};
  const accountDetails = Array.isArray(caisAccount?.CAIS_Account_DETAILS)
    ? caisAccount.CAIS_Account_DETAILS
    : [];
  const capsSummary = creditReport?.CAPS?.CAPS_Summary || {};
  const score = getScore(base, creditReport);

  return {
    score,
    scoreLabel: getScoreLabel(score),
    reportDate: formatDate(header?.ReportDate || base?.report_date),
    fetchDate: formatDate(meta.fetchDate || new Date()),
    fetchedBy: meta.fetchedBy || Sessions.userFullName || "Admin",
    summary: [
      {
        icon: <PersonOutlineOutlinedIcon />,
        label: "Name",
        value: base?.name || applicant?.First_Name || formData.fullName || "-",
      },
      {
        icon: <BadgeOutlinedIcon />,
        label: "PAN",
        value: base?.pan || applicant?.IncomeTaxPan || formData.pan || "-",
      },
      {
        icon: <PhoneIphoneOutlinedIcon />,
        label: "Mobile",
        value: base?.mobile || applicant?.Telephone_Number_Applicant_1st || formData.mobile || "-",
      },
      { icon: <WcOutlinedIcon />, label: "Gender", value: getGender(applicant, gender) },
    ],
    address: [
      { icon: <HomeOutlinedIcon />, label: "City", value: address?.City || "-" },
      { icon: <HomeOutlinedIcon />, label: "State", value: address?.State || "-" },
      { icon: <HomeOutlinedIcon />, label: "Pin Code", value: address?.PINCode || "-" },
      { icon: <HomeOutlinedIcon />, label: "Country", value: address?.Country_Code || "INDIA" },
    ],
    creditSummary: [
      {
        icon: <AccountBalanceOutlinedIcon />,
        label: "Total Accounts",
        value: caisSummary?.Credit_Account?.CreditAccountTotal ?? accountDetails.length ?? "-",
      },
      {
        icon: <AccountBalanceOutlinedIcon />,
        label: "Active Accounts",
        value: caisSummary?.Credit_Account?.CreditAccountActive ?? "-",
      },
      {
        icon: <AccountBalanceOutlinedIcon />,
        label: "Closed Accounts",
        value: caisSummary?.Credit_Account?.CreditAccountClosed ?? "-",
      },
      {
        icon: <AccountBalanceOutlinedIcon />,
        label: "Default Accounts",
        value: caisSummary?.Credit_Account?.CreditAccountDefault ?? "-",
      },
      {
        icon: <AccountBalanceOutlinedIcon />,
        label: "Secured Outstanding",
        value: formatCurrency(caisSummary?.Total_Outstanding_Balance?.Outstanding_Balance_Secured),
        highlight: true,
      },
      {
        icon: <AccountBalanceOutlinedIcon />,
        label: "Unsecured Outstanding",
        value: formatCurrency(
          caisSummary?.Total_Outstanding_Balance?.Outstanding_Balance_UnSecured
        ),
        warning: true,
      },
    ],
    enquiries: [
      {
        icon: <CreditScoreOutlinedIcon />,
        label: "Last 7 Days",
        value: capsSummary?.CAPSLast7Days ?? "-",
      },
      {
        icon: <CreditScoreOutlinedIcon />,
        label: "Last 30 Days",
        value: capsSummary?.CAPSLast30Days ?? "-",
      },
      {
        icon: <CreditScoreOutlinedIcon />,
        label: "Last 90 Days",
        value: capsSummary?.CAPSLast90Days ?? "-",
      },
      {
        icon: <CreditScoreOutlinedIcon />,
        label: "Default Accounts",
        value: caisSummary?.Credit_Account?.CreditAccountDefault ?? "-",
      },
      {
        icon: <CreditScoreOutlinedIcon />,
        label: "Amount Financed",
        value: formatCurrency(currentApplication?.Amount_Financed),
        highlight: true,
      },
      {
        icon: <CreditScoreOutlinedIcon />,
        label: "Enquiry Purpose",
        value: currentApplication?.Finance_Purpose || "-",
        warning: true,
      },
    ],
    accounts: accountDetails
      .slice()
      .sort((a, b) => new Date(b?.Date_Reported || 0) - new Date(a?.Date_Reported || 0))
      .slice(0, 2)
      .map((account) => ({
        subscriber: account?.Subscriber_Name || "-",
        rows: [
          { label: "Subscriber", value: account?.Subscriber_Name || "-" },
          { label: "Account Type", value: account?.Account_Type || "-" },
          {
            label: "Sanctioned Limit",
            value: formatCurrency(
              account?.Highest_Credit_or_Original_Loan_Amount || account?.Credit_Limit_Amount
            ),
          },
          { label: "Current Balance", value: formatCurrency(account?.Current_Balance) },
          { label: "Past Due", value: formatCurrency(account?.Amount_Past_Due) },
          { label: "Status", value: account?.Account_Status || account?.Payment_Rating || "-" },
          { label: "Reported", value: formatDate(account?.Date_Reported) },
        ],
      })),
  };
};

const inputStyles = {
  "& .MuiOutlinedInput-root": {
    borderRadius: "4px",
    backgroundColor: Colors.WHITE,
  },
  "& .MuiInputBase-input": {
    fontSize: "13px",
    py: 1.2,
  },
};

const selectableControlStyles = {
  color: "#d0d5dd",
  "&:after": {
    backgroundImage: `linear-gradient(195deg, ${Colors.LIGHT_BLUE}, ${Colors.LIGHT_BLUE}) !important`,
    transition: "none !important",
  },
  "& .MuiSvgIcon-root": {
    color: "transparent",
    fill: "transparent",
    borderColor: "#d0d5dd",
  },
  "&.Mui-checked": {
    color: Colors.LIGHT_BLUE,
  },
  "&.Mui-checked .MuiSvgIcon-root": {
    color: "transparent",
    fill: "transparent",
    borderColor: `${Colors.LIGHT_BLUE} !important`,
  },
  "&.Mui-checked:after": {
    backgroundImage: `linear-gradient(195deg, ${Colors.LIGHT_BLUE}, ${Colors.LIGHT_BLUE}) !important`,
  },
};

const checkboxControlStyles = {
  ...selectableControlStyles,
  "&.Mui-checked .MuiSvgIcon-root": {
    ...selectableControlStyles["&.Mui-checked .MuiSvgIcon-root"],
    backgroundImage: `url("data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 -1 22 22'%3e%3cpath fill='none' stroke='%23fff' stroke-linecap='round' stroke-linejoin='round' stroke-width='2.5' d='M6 10l3 3l6-6'/%3e%3c/svg%3e"), linear-gradient(195deg, ${Colors.LIGHT_BLUE}, ${Colors.LIGHT_BLUE}) !important`,
  },
};

function BenefitItem({ icon, title, caption }) {
  return (
    <Stack
      direction="row"
      spacing={1.25}
      alignItems="center"
      sx={{
        minHeight: 48,
        px: 1.5,
        py: 1,
        borderRadius: "4px",
        backgroundColor: "#f2f6ff",
      }}
    >
      <Box
        sx={{
          width: 24,
          height: 24,
          borderRadius: "50%",
          display: "grid",
          placeItems: "center",
          color: Colors.WHITE,
          backgroundColor: Colors.PRIMARY,
          flex: "0 0 auto",
          "& svg": { fontSize: 16 },
        }}
      >
        {icon}
      </Box>
      <Box>
        <Typography sx={{ fontSize: 12, fontWeight: 800, lineHeight: 1.2 }}>{title}</Typography>
        {caption ? (
          <Typography sx={{ fontSize: 9.5, color: "#667085", lineHeight: 1.2 }}>
            {caption}
          </Typography>
        ) : null}
      </Box>
    </Stack>
  );
}

function ScoreGauge({ score = 772, label = "Excellent" }) {
  const minScore = 300;
  const maxScore = 900;

  const normalized = Math.max(0, Math.min(1, (score - minScore) / (maxScore - minScore)));

  // SVG arc dimensions
  const cx = 150;
  const cy = 150;
  const r = 110;

  // Left (180°) → Right (0°)
  const angle = Math.PI - normalized * Math.PI;

  const markerX = cx + r * Math.cos(angle);
  const markerY = cy - r * Math.sin(angle);

  return (
    <Box
      sx={{
        width: 320,
        mx: "auto",
        position: "relative",
        textAlign: "center",
      }}
    >
      {/* Top Badge */}
      <Box
        sx={{
          position: "absolute",
          top: 0,
          left: "50%",
          transform: "translateX(-50%)",
          px: 1.5,
          py: 0.4,
          borderRadius: "4px",
          backgroundColor: "#EEF2F6",
          color: "#667085",
          fontSize: 13,
          fontWeight: 700,
          zIndex: 5,
        }}
      >
        600
      </Box>

      <Box sx={{ position: "relative", height: 210, mt: 2 }}>
        <svg width="100%" height="210" viewBox="0 0 300 210">
          <defs>
            <linearGradient id="scoreGradient" x1="0%" y1="0%" x2="100%" y2="0%">
              <stop offset="0%" stopColor="#F04438" />
              <stop offset="35%" stopColor="#F97316" />
              <stop offset="60%" stopColor="#D4C24F" />
              <stop offset="100%" stopColor="#22C55E" />
            </linearGradient>
          </defs>

          <path
            d="M 40 150 A 110 110 0 0 1 260 150"
            fill="none"
            stroke="#EAECF0"
            strokeWidth="24"
            strokeLinecap="round"
          />

          <path
            d="M 40 150 A 110 110 0 0 1 260 150"
            fill="none"
            stroke="url(#scoreGradient)"
            strokeWidth="24"
            strokeLinecap="round"
            strokeDasharray="345.5"
            strokeDashoffset={345.5 * (1 - normalized)}
            style={{ transition: "stroke-dashoffset 1s ease-in-out" }}
          />

          <circle cx={markerX} cy={markerY} r="8" fill="#FFFFFF" stroke="#101828" strokeWidth="4" />
        </svg>

        <Box
          sx={{
            position: "absolute",
            bottom: 25,
            left: "50%",
            transform: "translateX(-50%)",
            textAlign: "center",
          }}
        >
          <Typography
            sx={{
              fontSize: 42,
              fontWeight: 900,
              color: "#101828",
              lineHeight: 1,
            }}
          >
            {score}
          </Typography>

          <Typography
            sx={{
              mt: 1,
              fontSize: 18,
              fontWeight: 700,
              color: "#1FA94C",
            }}
          >
            {label}
          </Typography>
        </Box>

        {/* Left Badge */}
        <Box
          sx={{
            position: "absolute",
            left: 8,
            bottom: 30,
            px: 1.5,
            py: 0.4,
            borderRadius: "4px",
            backgroundColor: "#EEF2F6",
            color: "#667085",
            fontSize: 13,
            fontWeight: 700,
          }}
        >
          300
        </Box>

        {/* Right Badge */}
        <Box
          sx={{
            position: "absolute",
            right: 8,
            bottom: 30,
            px: 1.5,
            py: 0.4,
            borderRadius: "4px",
            backgroundColor: "#EEF2F6",
            color: "#667085",
            fontSize: 13,
            fontWeight: 700,
          }}
        >
          900
        </Box>
      </Box>
    </Box>
  );
}

function MetaCard({ icon, label, value, badge }) {
  return (
    <Stack
      direction="row"
      spacing={1.2}
      alignItems="center"
      sx={{
        minHeight: 58,
        p: 1.2,
        border: "1px solid #edf1f7",
        borderRadius: "4px",
        backgroundColor: Colors.WHITE,
      }}
    >
      <Box
        sx={{
          width: 28,
          height: 28,
          borderRadius: "4px",
          display: "grid",
          placeItems: "center",
          color: Colors.PRIMARY,
          backgroundColor: "#eef4ff",
          "& svg": { fontSize: 17 },
        }}
      >
        {icon}
      </Box>
      <Box sx={{ minWidth: 0 }}>
        <Typography sx={{ fontSize: 10, color: "#667085", lineHeight: 1.2 }}>{label}</Typography>
        <Stack direction="row" spacing={0.75} alignItems="center">
          <Typography sx={{ fontSize: 12, color: "#182033", fontWeight: 800 }}>{value}</Typography>
          {badge ? (
            <Box
              sx={{
                px: 0.65,
                py: 0.15,
                borderRadius: "3px",
                fontSize: 8,
                lineHeight: 1.4,
                color: "#02a83f",
                backgroundColor: "#dcfce7",
                fontWeight: 800,
              }}
            >
              {badge}
            </Box>
          ) : null}
        </Stack>
      </Box>
    </Stack>
  );
}

function InfoTile({ item }) {
  return (
    <Stack
      direction="row"
      spacing={1}
      alignItems="center"
      sx={{
        minHeight: 48,
        p: 1.1,
        borderRadius: "4px",
        backgroundColor: item.highlight ? "#eafaf0" : item.warning ? "#fff7e8" : "#f3f7ff",
      }}
    >
      <Box
        sx={{
          width: 24,
          height: 24,
          borderRadius: "4px",
          display: "grid",
          placeItems: "center",
          color: item.highlight ? "#02a83f" : item.warning ? "#d97706" : Colors.PRIMARY,
          backgroundColor: item.highlight ? "#dcfce7" : item.warning ? "#fef3c7" : "#eef4ff",
          flex: "0 0 auto",
          "& svg": { fontSize: 15 },
        }}
      >
        {item.icon}
      </Box>
      <Box sx={{ minWidth: 0 }}>
        <Typography sx={{ fontSize: 9.5, color: "#667085", lineHeight: 1.2 }}>
          {item.label}
        </Typography>
        <Typography sx={{ fontSize: 11, fontWeight: 800, color: "#182033", lineHeight: 1.2 }}>
          {item.value}
        </Typography>
      </Box>
    </Stack>
  );
}

function ReportSection({ title, children }) {
  const [open, setOpen] = useState(true);

  return (
    <Paper
      elevation={0}
      sx={{
        border: "1px solid #edf1f7",
        borderRadius: "4px",
        overflow: "hidden",
        backgroundColor: Colors.WHITE,
      }}
    >
      <Button
        fullWidth
        onClick={() => setOpen((prev) => !prev)}
        endIcon={
          <ExpandMoreIcon
            sx={{
              transform: open ? "rotate(180deg)" : "rotate(0deg)",
              transition: "transform 0.2s ease",
            }}
          />
        }
        sx={{
          justifyContent: "space-between",
          px: 1.6,
          py: 1.2,
          color: "#182033",
          textTransform: "none",
          fontSize: 12,
          fontWeight: 800,
        }}
      >
        {title}
      </Button>
      <Collapse in={open} timeout="auto" unmountOnExit>
        <Box sx={{ px: 1.6, pb: 1.6 }}>{children}</Box>
      </Collapse>
    </Paper>
  );
}

function AccountCard({ account }) {
  return (
    <Paper
      elevation={0}
      sx={{ border: "1px solid #edf1f7", borderRadius: "4px", overflow: "hidden" }}
    >
      <Box sx={{ p: 1.1, backgroundColor: "#f3f7ff" }}>
        <Typography sx={{ fontSize: 10, color: "#667085" }}>Subscriber</Typography>
        <Typography sx={{ fontSize: 11, fontWeight: 800, color: "#26334d" }} noWrap>
          {account.subscriber}
        </Typography>
      </Box>
      {account.rows.slice(1).map((row) => (
        <Stack
          key={`${row.label}-${row.value}`}
          direction="row"
          justifyContent="space-between"
          spacing={1}
          sx={{ px: 1.1, py: 0.75, borderTop: "1px solid #edf1f7" }}
        >
          <Typography sx={{ fontSize: 9.5, color: "#667085" }}>{row.label}</Typography>
          <Typography sx={{ fontSize: 10, fontWeight: 800, color: "#26334d", textAlign: "right" }}>
            {row.value}
          </Typography>
        </Stack>
      ))}
    </Paper>
  );
}

function InitialCreditScoreContent({
  actionType,
  agreement,
  formData,
  gender,
  isFormValid,
  loading,
  onActionTypeChange,
  onAgreementChange,
  onChange,
  onGenderChange,
  onSubmit,
}) {
  return (
    <Paper elevation={0} sx={{ p: { xs: 2, md: 3 }, borderRadius: "4px", minHeight: 520 }}>
      <Grid container spacing={{ xs: 3, md: 6 }} alignItems="flex-start">
        <Grid item xs={12} md={6}>
          <Stack spacing={2.2} sx={{ maxWidth: 430 }}>
            <Box
              sx={{
                width: "fit-content",
                px: 1,
                py: 0.45,
                borderRadius: "3px",
                backgroundColor: "#eef4ff",
                color: Colors.PRIMARY,
                fontSize: 10,
                fontWeight: 800,
              }}
            >
              100 % Free + Instant + Secure
            </Box>
            <Box>
              <Typography
                sx={{
                  fontSize: { xs: 26, md: 31 },
                  fontWeight: 900,
                  color: "#111827",
                  lineHeight: 1.1,
                }}
              >
                Check Your
              </Typography>
              <Typography
                sx={{
                  fontSize: { xs: 26, md: 31 },
                  fontWeight: 900,
                  color: "#111827",
                  lineHeight: 1.1,
                }}
              >
                <Box component="span" sx={{ color: Colors.PRIMARY }}>
                  Credit Score
                </Box>{" "}
                Instantly Today
              </Typography>
            </Box>
            <Typography sx={{ fontSize: 12, color: "#667085", maxWidth: 400 }}>
              Get your credit score in seconds and take control of your financial future. No impact
              on your score and completely secure.
            </Typography>
            <Grid container spacing={1.5}>
              <Grid item xs={12} sm={4}>
                <BenefitItem
                  icon={<CheckCircleOutlinedIcon />}
                  title="No Impact"
                  caption="on Score"
                />
              </Grid>
              <Grid item xs={12} sm={4}>
                <BenefitItem icon={<LockOutlinedIcon />} title="Instant" caption="Result" />
              </Grid>
              <Grid item xs={12} sm={4}>
                <BenefitItem icon={<ShieldOutlinedIcon />} title="Secure &" caption="Reliable" />
              </Grid>
              <Grid item xs={12}>
                <BenefitItem
                  icon={<ShieldOutlinedIcon />}
                  title="Your Privacy Comes First"
                  caption="All information is securely encrypted and protected at every step."
                />
              </Grid>
            </Grid>
          </Stack>
        </Grid>

        <Grid item xs={12} md={6}>
          <Stack spacing={1.8} sx={{ maxWidth: 430, ml: { md: "auto" } }}>
            {/* Gender Selector */}
            <FormControl component="fieldset" sx={{ m: 0, p: 0, mb: "-10px" }}>
              <FormLabel
                component="legend"
                sx={{
                  fontSize: 12,
                  fontWeight: 800,
                  mb: 0,
                  color: "#000000",
                  "&.Mui-focused": { color: "#000000" },
                }}
              >
                Gender
              </FormLabel>
              <RadioGroup
                row
                value={gender}
                onChange={(e) => onGenderChange(e.target.value)}
                sx={{ my: -0.5 }}
              >
                <FormControlLabel
                  value="male"
                  control={<Radio size="small" sx={selectableControlStyles} />}
                  label="Male"
                  sx={{ "& .MuiFormControlLabel-label": { fontSize: "12px" } }}
                />
                <FormControlLabel
                  value="female"
                  control={<Radio size="small" sx={selectableControlStyles} />}
                  label="Female"
                  sx={{ "& .MuiFormControlLabel-label": { fontSize: "12px" } }}
                />
              </RadioGroup>
            </FormControl>

            <TextField
              fullWidth
              placeholder="First Name"
              name="fullName"
              value={formData.fullName}
              onChange={onChange}
              sx={inputStyles}
            />
            <TextField
              fullWidth
              placeholder="Mobile Number"
              name="mobile"
              value={formData.mobile}
              onChange={onChange}
              sx={inputStyles}
            />
            <TextField
              fullWidth
              placeholder="Email Address"
              name="email"
              value={formData.email}
              onChange={onChange}
              sx={inputStyles}
            />
            <TextField
              fullWidth
              placeholder="PAN Number"
              name="pan"
              value={formData.pan}
              onChange={onChange}
              sx={inputStyles}
            />

            {/* Action Type Radio Selector (Download vs View) */}
            <FormControl component="fieldset" sx={{ m: 0, p: 0, mb: "-10px" }}>
              <FormLabel
                component="legend"
                sx={{
                  fontSize: 12,
                  fontWeight: 800,
                  mb: 0,
                  color: "#000000",
                  "&.Mui-focused": { color: "#000000" },
                }}
              >
                Select Option
              </FormLabel>
              <RadioGroup
                row
                value={actionType}
                onChange={(e) => onActionTypeChange(e.target.value)}
                sx={{ my: -0.5 }}
              >
                <FormControlLabel
                  value="download"
                  control={<Radio size="small" sx={selectableControlStyles} />}
                  label="Download PDF (Default)"
                  sx={{ "& .MuiFormControlLabel-label": { fontSize: "12px" } }}
                />
                <FormControlLabel
                  value="view"
                  control={<Radio size="small" sx={selectableControlStyles} />}
                  label="View Report"
                  sx={{ "& .MuiFormControlLabel-label": { fontSize: "12px" } }}
                />
              </RadioGroup>
            </FormControl>

            <Stack direction="row" spacing={1} alignItems="flex-start">
              <Checkbox
                size="small"
                checked={agreement}
                onChange={(e) => onAgreementChange(e.target.checked)}
                sx={{ ...checkboxControlStyles, p: 0, mt: 0.15, border: "none" }}
              />
              <Typography sx={{ fontSize: 10.5, color: "#667085", lineHeight: 1.45 }}>
                By continuing, you consent to FinVibes retrieving your Credit Score/Profile and
                accept the Terms of Use.
              </Typography>
            </Stack>

            <Button
              variant="contained"
              fullWidth
              onClick={onSubmit}
              disabled={!isFormValid || loading}
              sx={{
                minHeight: 40,
                borderRadius: "4px",
                textTransform: "none",
                fontSize: 12,
                fontWeight: 800,
                backgroundColor: Colors.PRIMARY,
                color: Colors.WHITE,
                "&:hover": { backgroundColor: Colors.PRIMARY },
              }}
            >
              {loading ? (
                <CircularProgress size={18} color="inherit" />
              ) : actionType === "download" ? (
                "Download Credit Report (PDF)"
              ) : (
                "Get Free Credit Report"
              )}
            </Button>
          </Stack>
        </Grid>
      </Grid>
    </Paper>
  );
}

export function CreditReportContent({ report, onDownloadPdf, pdfLoading }) {
  return (
    <Paper elevation={0} sx={{ p: { xs: 2, md: 3 }, borderRadius: "4px" }}>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={1.5}>
        <Typography sx={{ fontSize: 16, fontWeight: 900, color: "#182033" }}>
          Credit Report
        </Typography>
        {onDownloadPdf && (
          <Button
            variant="contained"
            size="small"
            onClick={onDownloadPdf}
            disabled={pdfLoading}
            startIcon={
              pdfLoading ? (
                <CircularProgress size={14} color="inherit" />
              ) : (
                <DownloadIcon fontSize="small" />
              )
            }
            sx={{
              backgroundColor: Colors.PRIMARY,
              color: Colors.WHITE,
              fontSize: 12,
              fontWeight: 700,
              textTransform: "none",
              "&:hover": { backgroundColor: Colors.PRIMARY },
            }}
          >
            {pdfLoading ? "Downloading..." : "Download PDF"}
          </Button>
        )}
      </Box>
      <Divider sx={{ mb: 3 }} />

      <Grid container spacing={{ xs: 2, md: 4 }} alignItems="center">
        <Grid item xs={12} md={5}>
          <ScoreGauge score={report.score} label={report.scoreLabel} />
        </Grid>
        <Grid item xs={12} md={7}>
          <Grid container spacing={1.5}>
            <Grid item xs={12} sm={6}>
              <MetaCard
                icon={<CalendarTodayOutlinedIcon />}
                label="Report Date"
                value={report.reportDate}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <MetaCard
                icon={<CreditScoreOutlinedIcon />}
                label="Score"
                value={report.score}
                badge={report.scoreLabel}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <MetaCard
                icon={<CalendarTodayOutlinedIcon />}
                label="Fetched On"
                value={report.fetchDate}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <MetaCard
                icon={<PersonOutlineOutlinedIcon />}
                label="Fetched By"
                value={report.fetchedBy}
              />
            </Grid>
          </Grid>
        </Grid>
      </Grid>

      <Grid container spacing={2} sx={{ mt: 2 }}>
        <Grid item xs={12} md={6}>
          <Stack spacing={2}>
            <ReportSection title="Summary">
              <Grid container spacing={1.2}>
                {report.summary.map((item) => (
                  <Grid item xs={12} sm={6} key={item.label}>
                    <InfoTile item={item} />
                  </Grid>
                ))}
              </Grid>
            </ReportSection>
            <ReportSection title="Address">
              <Grid container spacing={1.2}>
                {report.address.map((item) => (
                  <Grid item xs={12} sm={6} key={item.label}>
                    <InfoTile item={item} />
                  </Grid>
                ))}
              </Grid>
            </ReportSection>
            <ReportSection title="Recent Enquiries">
              <Grid container spacing={1.2}>
                {report.enquiries.map((item) => (
                  <Grid item xs={12} sm={6} key={item.label}>
                    <InfoTile item={item} />
                  </Grid>
                ))}
              </Grid>
            </ReportSection>
          </Stack>
        </Grid>

        <Grid item xs={12} md={6}>
          <Stack spacing={2}>
            <ReportSection title="Credit Summary">
              <Grid container spacing={1.2}>
                {report.creditSummary.map((item) => (
                  <Grid item xs={12} sm={6} key={item.label}>
                    <InfoTile item={item} />
                  </Grid>
                ))}
              </Grid>
            </ReportSection>
            <ReportSection title="Recent Loan Accounts">
              {report.accounts.length ? (
                <Grid container spacing={1.2}>
                  {report.accounts.map((account, index) => (
                    <Grid item xs={12} sm={6} key={`${account.subscriber}-${index}`}>
                      <AccountCard account={account} />
                    </Grid>
                  ))}
                </Grid>
              ) : (
                <Typography sx={{ fontSize: 12, color: "#667085" }}>
                  No recent loan accounts found.
                </Typography>
              )}
            </ReportSection>
          </Stack>
        </Grid>
      </Grid>
    </Paper>
  );
}

function CreditScoreForm() {
  const [actionType, setActionType] = useState("download");
  const [gender, setGender] = useState("male");
  const [agreement, setAgreement] = useState(false);
  const [loading, setLoading] = useState(false);
  const [pdfLoading, setPdfLoading] = useState(false);
  const [report, setReport] = useState(null);
  const dispatch = useDispatch();

  const [formData, setFormData] = useState({
    fullName: "",
    mobile: "",
    email: "",
    pan: "",
  });

  const isFormValid = useMemo(
    () =>
      agreement &&
      formData.fullName.trim().length > 0 &&
      formData.mobile.trim().length === 10 &&
      formData.pan.trim().length === 10,
    [agreement, formData.fullName, formData.mobile, formData.pan]
  );

  const handleChange = (e) => {
    const { name, value } = e.target;

    if (name === "mobile") {
      setFormData((prev) => ({ ...prev, mobile: value.replace(/\D/g, "").slice(0, 10) }));
      return;
    }

    if (name === "pan") {
      setFormData((prev) => ({
        ...prev,
        pan: value.toUpperCase().replace(/\s/g, "").slice(0, 10),
      }));
      return;
    }

    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const triggerPdfDownload = async (url, fileName) => {
    try {
      const res = await fetch(url, { mode: "cors" });
      const blob = await res.blob();
      const pdfBlob = new Blob([blob], { type: "application/pdf" });

      const link = document.createElement("a");
      link.href = URL.createObjectURL(pdfBlob);
      link.download = fileName.endsWith(".pdf") ? fileName : `${fileName}.pdf`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      URL.revokeObjectURL(link.href);
    } catch (err) {
      console.error("PDF download error, opening link in window:", err);
      window.open(url, "_blank");
    }
  };

  const handleDownloadPdf = async () => {
    if (!formData.mobile || !formData.pan) return;
    setPdfLoading(true);
    const payload = {
      type: "pdf",
      payload: {
        mobile: formData.mobile,
        pan: formData.pan,
        name: formData.fullName,
        gender,
        consent: "Y",
      },
    };
    try {
      const res = await dispatch(fetchCibil({ body: payload }));
      console.log("CIBIL Download PDF Response:", res);
      if (res.payload?.status === 201 || res.payload?.status === 200) {
        const resData = res.payload?.data?.data || res.payload?.data || {};
        const pdfUrl =
          resData?.credit_report_link ||
          resData?.pdfUrl ||
          resData?.url ||
          resData?.record?.pdfUrls?.[0]?.url;
        if (pdfUrl) {
          await triggerPdfDownload(
            pdfUrl,
            `Credit_Report_${formData.fullName.replace(/\s+/g, "_") || "User"}.pdf`
          );
        } else {
          alert("PDF report link not found in response.");
        }
      } else {
        const errMsg =
          res.payload?.data?.message ||
          res.payload?.data?.error?.message ||
          (typeof res.payload?.data?.error === "string" ? res.payload.data.error : null) ||
          "Failed to download PDF report.";
        console.error("Download PDF Error Details:", res.payload);
        alert(`Error (${res.payload?.status || "500"}): ${errMsg}`);
      }
    } catch (error) {
      console.error("Download PDF exception:", error);
      alert(`Failed to download PDF report: ${error.message}`);
    } finally {
      setPdfLoading(false);
    }
  };

  const handleSubmit = async () => {
    if (!isFormValid) return;

    setLoading(true);

    const payload = {
      type: actionType === "download" ? "pdf" : "data",
      payload: {
        mobile: formData.mobile,
        pan: formData.pan,
        name: formData.fullName,
        gender,
        consent: agreement ? "Y" : "N",
      },
    };

    try {
      const res = await dispatch(fetchCibil({ body: payload }));
      console.log("CIBIL Submit Response:", res);

      if (res.payload?.status === 201 || res.payload?.status === 200) {
        const resData = res.payload?.data?.data || res.payload?.data || {};

        if (actionType === "download") {
          const pdfUrl =
            resData?.credit_report_link ||
            resData?.pdfUrl ||
            resData?.url ||
            resData?.record?.pdfUrls?.[0]?.url;

          if (pdfUrl) {
            await triggerPdfDownload(
              pdfUrl,
              `Credit_Report_${formData.fullName.replace(/\s+/g, "_") || "User"}.pdf`
            );
          } else {
            setReport(normalizeReport(resData, formData, gender));
          }
        } else {
          setReport(normalizeReport(resData, formData, gender));
        }
      } else {
        const errMsg =
          res.payload?.data?.message ||
          res.payload?.data?.error?.message ||
          (typeof res.payload?.data?.error === "string" ? res.payload.data.error : null) ||
          "Unable to fetch credit report.";
        console.error("CIBIL Submit Error Details:", res.payload);
        alert(`CIBIL Error (${res.payload?.status || "500"}): ${errMsg}`);
      }
    } catch (error) {
      console.error("CIBIL Submit Exception:", error);
      alert(`Failed to fetch credit report: ${error.message}`);
    } finally {
      setLoading(false);
    }
  };

  return (
    <DashboardLayout>
      <PageTitle title={PageTitles.CREDIT_SCORE} />
      <Divider sx={{ my: 2 }} />

      <Box sx={{ maxWidth: 1180, mx: "auto", width: "100%" }}>
        {report ? (
          <CreditReportContent
            report={report}
            onDownloadPdf={handleDownloadPdf}
            pdfLoading={pdfLoading}
          />
        ) : (
          <InitialCreditScoreContent
            actionType={actionType}
            agreement={agreement}
            formData={formData}
            gender={gender}
            isFormValid={isFormValid}
            loading={loading}
            onActionTypeChange={setActionType}
            onAgreementChange={setAgreement}
            onChange={handleChange}
            onGenderChange={setGender}
            onSubmit={handleSubmit}
          />
        )}
      </Box>
    </DashboardLayout>
  );
}

export default CreditScoreForm;
